import { useCallback, useEffect, useRef } from 'react'

import { PagesPageUpdateDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

const AUTOSAVE_DEBOUNCE_MS = 1500

interface UsePageAutosaveProps {
  pageId: number
}

interface UsePageAutosaveReturn {
  debouncedAutosave: (content: string, title: string) => void
  isUpdating: boolean
}

export const usePageAutosave = ({ pageId }: UsePageAutosaveProps): UsePageAutosaveReturn => {
  const autosaveTimeoutRef = useRef<NodeJS.Timeout>()

  const [updateContent, { loading: isUpdating }] = useMutation(PagesPageUpdateDocument, {
    extractorFn: (result) => result.handbookPagesMutations.updateHandbookpagesPage,
  })

  const handleAutosave = useCallback(
    async (contentValue: string, titleValue: string) => {
      await updateContent({
        variables: {
          content: contentValue,
          id: pageId,
          title: titleValue,
        },
      })
    },
    [updateContent, pageId]
  )

  const debouncedAutosave = useCallback(
    (contentValue: string, titleValue: string) => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current)
      }
      autosaveTimeoutRef.current = setTimeout(() => {
        handleAutosave(contentValue, titleValue)
      }, AUTOSAVE_DEBOUNCE_MS)
    },
    [handleAutosave]
  )

  // Cleanup autosave timeout on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current)
      }
    }
  }, [])

  return {
    debouncedAutosave,
    isUpdating,
  }
}
